import { useContext, useEffect, useState } from 'react'
import * as Sentry from '@sentry/react'
import { useLocation, useNavigate } from 'react-router-dom'
import AuthContext from '../../context/authContext'
import { useAuthMe } from '../api/authHooks'
import { useGetMfa } from '../api/userHooks'
import { AuthService } from '../../lib/api/auth.services'
import { shouldRedirectToCreateProfile } from '../../lib/utils/mappers/postRegisterMapper'
import { isAuthExcludedPathname } from '../../lib/utils/authPath'

interface UseAuthSessionParams {
	excludedAuthPaths: string[]
}

export const useAuthSession = ({ excludedAuthPaths }: UseAuthSessionParams) => {
	const {
		setCurrentUser,
		currentUser,
		mfaEnabled,
		setMfaEnabled,
		setIsAuthLoading,
		isAuthenticated,
	} = useContext(AuthContext)

	const location = useLocation()
	const navigate = useNavigate()
	const [redirectingToLogin, setRedirectingToLogin] = useState(false)

	const isExcludedAuthPath = isAuthExcludedPathname(
		location.pathname,
		excludedAuthPaths,
	)

	const authMeQuery = useAuthMe({
		enabled: !isExcludedAuthPath && !redirectingToLogin,
		retry: false,
		refetchOnMount: false,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
	})

	const user = authMeQuery.data?.data
	const isSessionAuthenticated = Boolean(
		!authMeQuery.error &&
		authMeQuery.isSuccess &&
		authMeQuery.data?.success &&
		user,
	)

	const isBlockedOrDeletedAuthError = Boolean(
		user?.isDeleted || user?.isDeactivated,
	)

	const authConnection = user?.connection || ''
	const shouldFetchMfa =
		isSessionAuthenticated &&
		Boolean(user?.id) &&
		mfaEnabled === null &&
		authConnection !== 'google-oauth2' &&
		authConnection !== 'apple'

	const mfaQuery = useGetMfa({
		options: {
			enabled: shouldFetchMfa,
			retry: false,
		},
	})

	const isEmptyUserResponse = authMeQuery.isSuccess && !authMeQuery.data?.data

	const shouldShowUserLoadError =
		isSessionAuthenticated &&
		(authMeQuery.isError || isEmptyUserResponse) &&
		!isExcludedAuthPath

	const shouldWaitForCurrentUser =
		isSessionAuthenticated &&
		!currentUser?.id &&
		!isExcludedAuthPath &&
		!shouldShowUserLoadError

	const needsCreateProfileRedirect = shouldRedirectToCreateProfile(currentUser)

	useEffect(() => {
		setIsAuthLoading(
			!isExcludedAuthPath && (authMeQuery.isLoading || redirectingToLogin),
		)
	}, [
		authMeQuery.isLoading,
		isExcludedAuthPath,
		redirectingToLogin,
		setIsAuthLoading,
	])

	useEffect(() => {
		if (
			isBlockedOrDeletedAuthError &&
			!location.pathname.includes('/user-blocked')
		) {
			navigate('/user-blocked', { replace: true })
		}
	}, [isBlockedOrDeletedAuthError, location.pathname, navigate])

	useEffect(() => {
		if (isExcludedAuthPath || redirectingToLogin) return
		if (authMeQuery.isLoading || !authMeQuery.isError || !authMeQuery.error) {
			return
		}

		setRedirectingToLogin(true)
		AuthService.login()
	}, [
		authMeQuery.error,
		authMeQuery.isError,
		authMeQuery.isLoading,
		isExcludedAuthPath,
		redirectingToLogin,
	])

	useEffect(() => {
		if (!user) return

		if (user.profile?.email) {
			Sentry.setUser({
				email: user.profile.email,
				username: `${user.profile.firstName || ''} ${
					user.profile.lastName || ''
				}`.trim(),
			})
		}

		setCurrentUser(user)
	}, [setCurrentUser, user, authMeQuery.data])

	useEffect(() => {
		if (!isSessionAuthenticated || mfaEnabled !== null) return

		if (authConnection === 'google-oauth2' || authConnection === 'apple') {
			setMfaEnabled(false)
			return
		}

		if (mfaQuery.data) {
			setMfaEnabled(Boolean(mfaQuery.data?.data?.value))
		} else if (mfaQuery.isError) {
			setMfaEnabled(false)
		}
	}, [
		isSessionAuthenticated,
		authConnection,
		mfaEnabled,
		mfaQuery.data,
		mfaQuery.isError,
		setMfaEnabled,
	])

	return {
		isEmptyUserResponse,
		currentUser,
		isAuthenticated: isAuthenticated || isSessionAuthenticated,
		isBlockedOrDeletedAuthError,
		isLoading: authMeQuery.isLoading || redirectingToLogin,
		needsCreateProfileRedirect,
		shouldShowUserLoadError,
		shouldWaitForCurrentUser,
		userProfileError: authMeQuery.error,
	}
}
